import { DateTime } from 'luxon'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import type { DonorDonation } from '@/types/donorFinancialYear'

/** Donation added by the donor (not processed or verified by Swiftaid). */
export function isDonorAddedDonation(donation: DonorDonation): boolean {
  return (
    donation.sourceCategory === 'manual' ||
    donation.source === 'manual' ||
    donation.id.startsWith('manual-')
  )
}

/** UK tax year bounds for an API period key (e.g. 2025-2026). */
export function periodKeyToBounds(periodKey: string): { start: DateTime; end: DateTime } | null {
  const parts = periodKey.split('-').map((p) => parseInt(p, 10))
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null
  const [startYear, endYear] = parts
  return {
    start: DateTime.fromObject(
      { year: startYear, month: 4, day: 6, hour: 0, minute: 0, second: 0 },
      { zone: 'Europe/London' }
    ),
    end: DateTime.fromObject(
      { year: endYear, month: 4, day: 5, hour: 23, minute: 59, second: 59 },
      { zone: 'Europe/London' }
    ),
  }
}

/** Whether a donation date falls within the given UK tax year period. */
export function donationInTaxYearPeriod(donation: DonorDonation, periodKey: string): boolean {
  const bounds = periodKeyToBounds(periodKey)
  if (!bounds) return false
  const dt = DateTime.fromISO(donation.time, { zone: 'Europe/London' })
  if (!dt.isValid) return false
  return dt >= bounds.start && dt <= bounds.end
}

export function donationsForTaxYearPeriod(
  donations: DonorDonation[],
  periodKey: string
): DonorDonation[] {
  return reverse(
    sortBy(
      donations.filter((d) => donationInTaxYearPeriod(d, periodKey)),
      'time'
    )
  )
}

export function sumDonationAmounts(donations: DonorDonation[]): number {
  return donations.reduce((sum, d) => sum + d.amount, 0)
}

export function sumGiftAidAmounts(donations: DonorDonation[]): number {
  return donations.reduce((sum, d) => sum + (d.giftaid || 0), 0)
}
